import { useAtom } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/core/hooks";
import { themeAtom } from "@/core/state";
import { Button, Infobulle } from "@/shared/ui";
import { Palette } from "@/shared/ui/icons";

import { PanneauLateral } from "./PanneauLateral";
import { ReglagesDuTheme, SectionLabel } from "./AtelierDuTheme";
import { ChoixDeLaLangue } from "./LanguagePickerModal";

/**
 * Bouton + modale « Personnaliser ».
 *
 * Un panneau à droite (`PanneauLateral`), les réglages dedans
 * (`ReglagesDuTheme` : mode, accent, fond, taille) — et le vrai cockpit à côté
 * comme aperçu : tout s'applique en direct.
 *
 * Le bouton vit dans la BottomBar (variant "bar") et la SideRail (variant
 * "rail").
 */
export function ThemePickerButton({
  variant = "bar",
}: {
  /** "bar" = libellé inline (BottomBar) ; "rail" = icône seule (SideRail). */
  variant?: "bar" | "rail";
} = {}) {
  const { t } = useTranslation();
  const [theme] = useAtom(themeAtom);
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const isRail = variant === "rail" && !isMobile;

  const libelle = t("shell.themePicker.ariaLabel");
  const nomDuTheme = t(`shell.themePicker.themes.${theme}`);

  return (
    <>
      {isMobile ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={libelle}
          className="mobile-tab-item h-12"
        >
          <Palette className="h-5 w-5" />
          <span className="mobile-tab-label text-[11px] capitalize">{nomDuTheme}</span>
        </button>
      ) : isRail ? (
        <Infobulle texte={t("shell.themePicker.title")} cote="droite">
          <Button
            variant="ghost"
            size="lg"
            iconOnly
            className="h-9 w-9"
            onClick={() => setOpen(true)}
            aria-label={libelle}
          >
            <Palette className="h-[18px] w-[18px]" />
          </Button>
        </Infobulle>
      ) : (
        <Infobulle texte={t("shell.themePicker.title")}>
          <Button
            variant="ghost"
            size="md"
            icon={<Palette className="h-3.5 w-3.5" />}
            onClick={() => setOpen(true)}
            aria-label={libelle}
          >
            <span className="capitalize">{nomDuTheme}</span>
          </Button>
        </Infobulle>
      )}

      <ThemeModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ThemeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!open) return null;
  // Pas de modale : un panneau à droite, et le VRAI cockpit à côté comme
  // aperçu (Kayla, 07-10-2026, le « Preview Theme » de Discord en exemple).
  return (
    <PanneauLateral titre={t("shell.themePicker.title")} fermer={onClose}>
      <div className="flex flex-col gap-6">
        <ReglagesDuTheme />
        <section className="flex flex-col gap-3">
          <SectionLabel>{t("shell.languagePicker.ariaLabel")}</SectionLabel>
          <ChoixDeLaLangue />
        </section>
      </div>
    </PanneauLateral>
  );
}
